import React from 'react';
import { LoadingSpinner } from './LoadingSpinner';

interface LoadingOverlayProps {
  isLoading: boolean;
  text?: string;
  variant?: 'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  children?: React.ReactNode;
  backdrop?: boolean;
  zIndex?: number;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  text = 'Loading...',
  variant = 'glass',
  size = 'lg',
  className = '',
  children,
  backdrop = true,
  zIndex = 50
}) => {
  if (!isLoading) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      {children}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ zIndex }}
      >
        {backdrop && (
          <div 
            className="absolute inset-0 bg-background-200/80 backdrop-blur-sm"
            style={{
              background: 'linear-gradient(135deg, rgba(15, 15, 15, 0.8) 0%, rgba(26, 26, 26, 0.9) 100%)'
            }}
          />
        )}
        <div className="relative z-10">
          <LoadingSpinner
            variant={variant}
            size={size}
            text={text}
            showText={!!text}
            className="text-center"
          />
        </div>
      </div>
    </div>
  );
};

// Full screen AI loading component
export const FullScreenLoader: React.FC<{
  text?: string;
  variant?: 'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showBackground?: boolean;
}> = ({ 
  text = 'AI Processing...', 
  variant = 'ai', 
  size = '2xl',
  showBackground = true
}) => {
  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{
        background: showBackground 
          ? 'linear-gradient(135deg, #0F0F0F 0%, #141414 50%, #1A1A1A 100%)'
          : 'transparent'
      }}
    >
      {/* AI-themed background pattern */}
      {showBackground && (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-cyan-400/5 rounded-full animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-24 h-24 bg-blue-500/5 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-1/4 left-1/3 w-16 h-16 bg-purple-400/5 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
      )}
      
      <div className="relative z-10 text-center">
        <div className="glass-card p-12 backdrop-blur-xl border border-glass-border/50">
          <LoadingSpinner
            variant={variant}
            size={size}
            text={text}
            showText={!!text}
            className="text-center"
          />
          
          {/* AI processing indicators */}
          <div className="mt-6 space-y-2">
            <div className="flex justify-center space-x-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"
                  style={{
                    animationDelay: `${i * 0.3}s`,
                    animationDuration: '1.5s'
                  }}
                />
              ))}
            </div>
            <p className="text-xs text-text-muted font-mono">
              Neural networks processing...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Button loading state
export const LoadingButton: React.FC<{
  isLoading: boolean;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'gradient' | 'success' | 'warning';
  size?: 'default' | 'sm' | 'lg' | 'xl' | 'icon';
  type?: 'button' | 'submit' | 'reset';
  title?: string;
}> = ({
  isLoading,
  children,
  className = '',
  disabled = false,
  onClick,
  variant = 'default',
  size = 'default',
  type = 'button',
  title
}) => {
  // Button variant styles (matching the original Button component)
  const getVariantClasses = (variant: string) => {
    switch (variant) {
      case 'default':
        return 'glass-button text-text-primary hover:shadow-glow-primary hover:-translate-y-1';
      case 'destructive':
        return 'bg-error-500 text-white hover:bg-error-600 hover:shadow-glow-error';
      case 'outline':
        return 'glass-button border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white hover:shadow-glow-primary';
      case 'secondary':
        return 'glass-button bg-glass-secondary text-text-primary hover:shadow-glow-primary';
      case 'ghost':
        return 'hover:bg-glass-primary hover:text-primary-500 text-text-secondary';
      case 'link':
        return 'text-primary-500 underline-offset-4 hover:underline';
      case 'gradient':
        return 'bg-gradient-primary text-white hover:shadow-neon hover:-translate-y-1';
      case 'success':
        return 'bg-success-500 text-white hover:bg-success-600 hover:shadow-glow-success';
      case 'warning':
        return 'bg-warning-500 text-white hover:bg-warning-600 hover:shadow-glow-warning';
      default:
        return 'glass-button text-text-primary hover:shadow-glow-primary hover:-translate-y-1';
    }
  };

  // Button size styles (matching the original Button component)
  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'default':
        return 'h-10 sm:h-11 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-body-md';
      case 'sm':
        return 'h-8 sm:h-9 rounded-lg px-2 sm:px-3 text-xs sm:text-body-sm';
      case 'lg':
        return 'h-11 sm:h-12 rounded-xl sm:rounded-2xl px-6 sm:px-8 text-base sm:text-body-lg';
      case 'xl':
        return 'h-12 sm:h-14 rounded-xl sm:rounded-2xl px-8 sm:px-10 text-lg sm:text-heading-sm';
      case 'icon':
        return 'h-10 w-10 sm:h-11 sm:w-11';
      default:
        return 'h-10 sm:h-11 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-body-md';
    }
  };

  const baseClasses = 'inline-flex items-center justify-center whitespace-nowrap rounded-xl font-semibold transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background-200 disabled:pointer-events-none disabled:opacity-50 hover:scale-102 active:scale-98';
  const variantClasses = getVariantClasses(variant);
  const sizeClasses = getSizeClasses(size);

  return (
    <button
      type={type}
      title={title}
      className={`${baseClasses} ${variantClasses} ${sizeClasses} relative ${className}`}
      disabled={disabled || isLoading}
      onClick={onClick}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner variant="ai" size="sm" />
        </div>
      )}
      <div className={isLoading ? 'opacity-0' : 'opacity-100'}>
        {children}
      </div>
    </button>
  );
};

export default LoadingOverlay;
